import { FC, lazy, Suspense, useEffect, useState } from 'react';
const ReactApexChart = lazy(() => import('react-apexcharts'));
import type { ApexOptions } from 'apexcharts';

interface DataItem {
  name?: string | null;
  revenue?: number | null;
}

interface PiChartProps {
  data?: DataItem[];
  isLoading?: boolean;
  surFix?: string;
}

const PiChart: FC<PiChartProps> = ({
  data = [],
  isLoading = false,
  surFix = '',
}) => {
  const [chartConfig, setChartConfig] = useState<{
    series: number[];
    options: ApexOptions;
  }>({
    series: [],
    options: {
      chart: {
        id: 'pie-chart',
        type: 'pie',
        height: 450,
      },
      labels: [],
      colors: [],
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val.toFixed(1)}%`,
        style: {
          fontWeight: 600,
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} ${surFix}`,
        },
      },
      legend: {
        position: 'top',
        offsetY: 10,
        itemMargin: {
          vertical: 6,
        },
      },
      stroke: {
        show: true,
        width: 1,
      },
    },
  });

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartConfig((prev) => ({
        ...prev,
        series: [],
        options: {
          ...prev.options,
          labels: [],
          colors: [],
        },
      }));
      return;
    }

    const labels = data.map((d) => d?.name ?? '');
    const values = data.map((d) => d?.revenue ?? 0);
    const CARD_BG_COLORS = [
      '#8EC3EA', // blue
      '#79C58F', // green
      '#E99674', // peach
      '#CC7FB0', // purple
      '#C84E4E', // red
    ];

    setChartConfig((prev) => ({
      ...prev,
      series: values,
      options: {
        ...prev.options,
        labels,
        colors: CARD_BG_COLORS,
      },
    }));
  }, [data]);

  return (
    <div id="pie-chart" className="w-full h-[400px] overflow-hidden">
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-10 w-10" />
        </div>
      ) : data && data.length > 0 ? (
        <Suspense
          fallback={
            <div className="h-full flex items-center justify-center">
              Loading…
            </div>
          }
        >
          <ReactApexChart
            options={chartConfig.options}
            series={chartConfig.series}
            type="pie"
            height={400}
          />
        </Suspense>
      ) : (
        <div className="flex justify-center items-center h-full rounded-lg bg-gray-50 text-gray-500">
          No data available
        </div>
      )}
    </div>
  );
};

export default PiChart;
